import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { tenantSelector } from '../../selector/authenticationSelector';
import { RootState } from '../../store/store';

/** Classes per placement — the desktop brand sits left in the bar, the mobile
 *  one in the centre of the shrinkable mobile row (see TopBar). */
const CLASSES = {
    desktop: {
        masterText: 'hidden lg:block text-lg font-semibold text-foreground truncate max-w-72',
        text: 'hidden lg:block text-lg font-semibold text-foreground truncate max-w-52',
        image: 'hidden lg:block max-h-6 max-w-52 object-contain text-foreground',
    },
    mobile: {
        masterText: 'min-w-0 text-base font-semibold text-foreground truncate max-w-[14rem]',
        text: 'min-w-0 text-base font-semibold text-foreground truncate max-w-[10rem]',
        image: 'min-w-0 max-h-6 max-w-52 object-contain',
    },
};

interface TopBarBrandProps {
    variant: 'desktop' | 'mobile';
    isInMasterMode: boolean;
}

/**
 * The tenant's mark in the top bar, in this order:
 *  1. master mode → "Master mode" as text;
 *  2. the logo the tenant uploaded (account settings → Branding);
 *  3. its organisation name as text — an onboarded tenant without a logo;
 *  4. the logo bundled with a legacy tenant's config.
 *
 * An uploaded logo that fails to load falls back to the name rather than
 * leaving a broken image where the brand should be.
 */
const TopBarBrand = ({ variant, isInMasterMode }: TopBarBrandProps) => {
    const { t } = useTranslation();
    const selectedTenant = useSelector(tenantSelector);
    const config = useSelector((state: RootState) => state.authentication.config);
    const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null);
    const classes = CLASSES[variant];

    if (isInMasterMode) {
        const masterBrand = t('master.brand');
        return <span className={classes.masterText} title={masterBrand}>{masterBrand}</span>;
    }

    const name = config?.organizationName;
    const uploadedLogo = config?.brandLogoUrl;
    if (uploadedLogo && uploadedLogo !== failedLogoUrl) {
        return (
            <img
                src={uploadedLogo}
                alt={name ?? selectedTenant ?? ''}
                title={name}
                className={classes.image}
                onError={() => setFailedLogoUrl(uploadedLogo)}
            />
        );
    }

    if (name) {
        return <span className={classes.text} title={name}>{name}</span>;
    }

    return (
        <img
            src={config?.logo}
            className={classes.image}
            alt={variant === 'desktop' ? (selectedTenant ?? 'kolula') : ''}
        />
    );
};

export default TopBarBrand;
